import React, { useState } from "react";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { useAPI } from "../../../contexts/APIContext";
import { showToast } from "../../Toast";
import { Mascot, StepHeading, ContinueButton, CtaRow, Item, Accent } from "../NOKit";

const INTEREST_EMOJIS = {
  Animals: "🐾", Space: "🚀", Dinosaurs: "🦕", Art: "🎨",
  Music: "🎵", Sports: "⚽️", Science: "🔬", Magic: "✨",
  Adventure: "🗺️", Fantasy: "🧚", Robots: "🤖", Nature: "🌿",
  Ocean: "🌊", Cooking: "👨‍🍳", Dancing: "💃", Reading: "📚",
  Building: "🏗️", Cars: "🚗", Superheroes: "🦸", Princess: "👸",
  Pirates: "🏴‍☠️", Knights: "⚔️", Dragons: "🐉", Unicorns: "🦄",
  Gaming: "🎮", Movies: "🎬", Travel: "✈️", Food: "🍕",
  Pets: "🐶", Fashion: "👗",
};

const getEmoji = (interest) => {
  if (INTEREST_EMOJIS[interest]) return INTEREST_EMOJIS[interest];
  const match = Object.keys(INTEREST_EMOJIS).find((key) => key.toLowerCase() === interest.toLowerCase());
  return match ? INTEREST_EMOJIS[match] : "⭐";
};

const MAX_INTERESTS = 3; // matches the iOS cap
const FIRST_PAGE = 12;

const SearchIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
  </svg>
);

const InterestsStep = () => {
  const { selectedInterests, setSelectedInterests, nextStep, username } = useOnboarding();
  const { apiClient } = useAPI();
  const [customInterest, setCustomInterest] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  // A tag tapped while the picks are full shakes instead of silently doing
  // nothing; the count line says what to do
  const [shaking, setShaking] = useState(null);
  const [fullNudge, setFullNudge] = useState(false);

  const atMax = selectedInterests.length >= MAX_INTERESTS;

  const nudgeFull = (tag) => {
    setShaking(tag);
    setFullNudge(true);
    setTimeout(() => setShaking(null), 350);
  };

  const toggleInterest = (interest) => {
    if (!selectedInterests.includes(interest) && atMax) {
      nudgeFull(interest);
      return;
    }
    setFullNudge(false);
    setSelectedInterests((prev) => {
      if (prev.includes(interest)) return prev.filter((i) => i !== interest);
      if (prev.length >= MAX_INTERESTS) return prev;
      return [...prev, interest];
    });
  };

  const addCustomInterest = async () => {
    const trimmed = customInterest.trim();
    if (!trimmed || selectedInterests.includes(trimmed)) return;
    if (atMax) {
      nudgeFull(null);
      return;
    }

    if (apiClient) {
      setIsChecking(true);
      try {
        const result = await apiClient.checkTextSafety(trimmed);
        if (result?.safe === false) {
          showToast("That interest isn't appropriate for Pixley. Try something else!", "error");
          setCustomInterest("");
          setIsChecking(false);
          return;
        }
      } catch (err) { /* allow through on API failure */ }
      setIsChecking(false);
    }

    setFullNudge(false);
    // Re-checked inside the update: a tag tapped during the safety check
    // may have filled the last slot
    setSelectedInterests((prev) =>
      prev.length >= MAX_INTERESTS || prev.includes(trimmed) ? prev : [...prev, trimmed]
    );
    setCustomInterest("");
  };

  const allInterests = [
    ...Object.keys(INTEREST_EMOJIS),
    ...selectedInterests.filter((i) => !INTEREST_EMOJIS[i]),
  ];
  const query = customInterest.trim().toLowerCase();
  const isSearching = query.length > 0;
  const filtered = isSearching
    ? allInterests.filter((i) => i.toLowerCase().includes(query))
    : allInterests;
  // The first page, plus anything already picked so a pick never hides
  const visible =
    isSearching || showAll
      ? filtered
      : filtered.filter((i, idx) => idx < FIRST_PAGE || selectedInterests.includes(i));
  const hidden = filtered.length - visible.length;

  const canContinue = selectedInterests.length > 0;
  const exactMatch = isSearching && allInterests.some((i) => i.toLowerCase() === query);

  return (
    <>
      <Mascot clip="panda_wave" size="small" />
      <StepHeading
        eyebrow="Interests"
        title={<>What does <Accent>{username || "your child"}</Accent> love?</>}
        subtitle={`Pick up to ${MAX_INTERESTS} — their stories are built around them.`}
      />

      <Item className="ob-search">
        <SearchIcon />
        <input
          type="text"
          value={customInterest}
          onChange={(e) => setCustomInterest(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustomInterest();
            }
          }}
          placeholder="Search, or add your own"
          aria-label="Search or add an interest"
          autoComplete="off"
        />
        {isSearching && !exactMatch && !atMax && (
          <button type="button" className="ob-search-add" onClick={addCustomInterest} disabled={isChecking}>
            {isChecking ? "…" : "+ Add"}
          </button>
        )}
      </Item>

      <Item className="ob-tags" role="group" aria-label="Interests">
        {visible.map((interest) => {
          const selected = selectedInterests.includes(interest);
          return (
            <button
              key={interest}
              type="button"
              role="checkbox"
              aria-checked={selected}
              className={`ob-tag${selected ? " is-selected" : ""}${!selected && atMax ? " is-dim" : ""}${shaking === interest ? " is-shake" : ""}`}
              onClick={() => toggleInterest(interest)}
            >
              <span className="ob-tag-emoji" aria-hidden="true">{getEmoji(interest)}</span>
              {interest}
            </button>
          );
        })}
        {!isSearching && hidden > 0 && (
          <button type="button" className="ob-tag is-more" onClick={() => setShowAll(true)}>
            +{hidden} more
          </button>
        )}
        {!isSearching && showAll && filtered.length > FIRST_PAGE && (
          <button type="button" className="ob-tag is-more" onClick={() => setShowAll(false)}>
            Show less
          </button>
        )}
        {isSearching && visible.length === 0 && (
          <span className="ob-count" style={{ margin: 0 }}>
            Nothing called that yet — add it above.
          </span>
        )}
      </Item>

      <Item className={`ob-count${fullNudge && atMax ? " is-full" : ""}`} aria-live="polite">
        <b>{selectedInterests.length}</b> of {MAX_INTERESTS} picked
        {atMax && <span className="ob-count-hint"> — that's the lot! Unpick one to swap.</span>}
      </Item>

      <CtaRow>
        <ContinueButton onClick={nextStep} disabled={!canContinue} />
      </CtaRow>
    </>
  );
};

export default InterestsStep;
